import type {ReactElement} from 'react';
import React from 'react';
import type {AddItemDropdownProps} from '@Components/poster-editor/components/poster-add-item-bar/poster-add-item-bar.types';
import type {ControlledListItemType} from '@Components/controlled-list/controlled-list.types';
import {CONTROLLED_LIST_ITEM_TYPE} from '@Components/controlled-list/controlled-list.types';
import {onOpenStockBackgroundModal, onOpenUserBackgroundUploadsModal, openColorBackgroundPanel} from '@Components/poster-editor/library/poster-editor-open-modals';
import {BACKGROUND_TYPE} from '@Panels/color-background-panel/color-background-type.types';
import {ThumbnailType} from '@Components/thumbnail/thumbnail.types';
import {AddItemThumbnail} from '@Components/poster-editor/components/poster-add-item-bar/components/add-item-thumbnail';
import {ControlledList} from '@Components/controlled-list';
import {noop} from '@Utils/general.util';
import {BackgroundTypeName} from '@PosterWhiteboard/page/background/background.class';
import {FillTypes} from '@PosterWhiteboard/classes/fill.class';
import {getFillColorForBackgroundDropdownItem} from '@PosterWhiteboard/page/background/color-background.class';
import {PageBackgroundDropDownItem} from '@Components/poster-editor/components/poster-editing-side-panel/components/page-background-option/page-background-option.types';
import {useAppSelector} from '@/hooks';

interface BackgroundDropdownListProps extends AddItemDropdownProps {}

export function BackgroundDropdownList({className = '', onOpenModal = noop}: BackgroundDropdownListProps): ReactElement<any> {
  const isPosterVideo = useAppSelector((state) => {
    return state.posterEditor.isVideo;
  });

  const applyTransparentBackground = (): void => {
    const currentPage = window.posterEditor?.whiteboard?.getCurrentPage();
    if (!currentPage) {
      throw new Error('current page does not exist');
    }

    void currentPage.background.updateFromObject({
      details: {
        type: BackgroundTypeName.COLOR,
        fill: {
          fillType: FillTypes.NONE,
          fillColor: getFillColorForBackgroundDropdownItem(PageBackgroundDropDownItem.TRANSPARENT),
        },
      },
    });
  };

  const options: ControlledListItemType[] = [];
  options.push({
    id: 'add-solid-background',
    type: CONTROLLED_LIST_ITEM_TYPE.DEFAULT_2,
    text: window.i18next.t('pmwjs_background_solid'),
    subText: window.i18next.t('pmwjs_add_solid_background'),
    onClick: (): void => {
      onOpenModal();
      openColorBackgroundPanel(BACKGROUND_TYPE.SOLID);
    },
    thumbnail: {
      type: ThumbnailType.CUSTOM,
      content: <AddItemThumbnail icon="icon-fill" />,
    },
  });

  options.push({
    id: 'add-gradient-background',
    type: CONTROLLED_LIST_ITEM_TYPE.DEFAULT_2,
    text: window.i18next.t('pmwjs_background_gradient'),
    subText: window.i18next.t('pmwjs_create_a_gradient_background'),
    onClick: (): void => {
      onOpenModal();
      openColorBackgroundPanel(BACKGROUND_TYPE.GRADIENT);
    },
    thumbnail: {
      type: ThumbnailType.CUSTOM,
      content: <AddItemThumbnail icon="icon-contrast" />,
    },
  });

  if (!isPosterVideo) {
    options.push({
      id: 'add-transparent-background',
      type: CONTROLLED_LIST_ITEM_TYPE.DEFAULT_2,
      text: window.i18next.t('pmwjs_transparent'),
      subText: window.i18next.t('pmwjs_add_transparent_background'),
      onClick: (): void => {
        onOpenModal();
        applyTransparentBackground();
      },
      thumbnail: {
        type: ThumbnailType.CUSTOM,
        content: <AddItemThumbnail icon="icon-opacity" />,
      },
    });
  }

  options.push({
    id: 'add-my-backgrounds',
    type: CONTROLLED_LIST_ITEM_TYPE.DEFAULT_2,
    text: window.i18next.t('pmwjs_my_backgrounds'),
    subText: window.i18next.t('pmwjs_add_from_your_uploads'),
    onClick: (): void => {
      onOpenModal();
      onOpenUserBackgroundUploadsModal();
    },
    thumbnail: {
      type: ThumbnailType.CUSTOM,
      content: <AddItemThumbnail icon="icon-upload-1" />,
    },
  });

  options.push({
    id: 'add-stock-photo-background',
    type: CONTROLLED_LIST_ITEM_TYPE.DEFAULT_2,
    text: window.i18next.t('pmwjs_stock_photo'),
    subText: window.i18next.t('pmwjs_search_add_stock_photos'),
    onClick: () => {
      onOpenModal();
      onOpenStockBackgroundModal();
    },
    thumbnail: {
      type: ThumbnailType.CUSTOM,
      content: <AddItemThumbnail icon="icon-stock-image" />,
    },
  });

  return <ControlledList items={options} className={`spacing-m-b-0 ${className}`} />;
}
